import React, { useMemo, useState } from "react";
import global from "@/utils/helpers/global";

const VB_W = 720;
const VB_H = 250;
const PAD_L = 34;
const PAD_R = 10;
const PAD_T = 22;
const PAD_B = 26;

function UsageChart({ items, months = 6 }) {
  const data = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    const mapped = list
      .map((item) => ({
        bulan: parseInt(item?.periode_bulan, 10),
        tahun: parseInt(item?.periode_tahun, 10),
        v: parseFloat(item?.m3_pemakaian),
      }))
      .filter(
        (d) =>
          Number.isFinite(d.v) &&
          Number.isFinite(d.bulan) &&
          Number.isFinite(d.tahun),
      );

    mapped.sort((a, b) => {
      if (a.tahun !== b.tahun) return a.tahun - b.tahun;
      return a.bulan - b.bulan;
    });

    return mapped.slice(-months);
  }, [items, months]);

  const [tip, setTip] = useState({
    show: false,
    left: 0,
    top: 0,
    label: "",
    value: 0,
  });

  if (data.length === 0) return null;

  const max = data.reduce((m, d) => Math.max(m, d.v), 0);
  const yMax = Math.max(5, Math.ceil(max / 5) * 5);
  const avg = data.reduce((sum, d) => sum + d.v, 0) / data.length;

  const plotW = VB_W - PAD_L - PAD_R;
  const plotH = VB_H - PAD_T - PAD_B;
  const baseY = PAD_T + plotH;
  const slotW = plotW / data.length;
  const barW = Math.max(14, Math.min(38, slotW * 0.56));
  const yOf = (v) => baseY - plotH * (v / yMax);

  const tickCount = 4;
  const ticks = Array.from({ length: tickCount + 1 }, (_, i) =>
    Math.round((yMax * i) / tickCount),
  );
  const avgY = yOf(avg);

  const handleEnter = (d, cx, y) => {
    setTip({
      show: true,
      left: (cx / VB_W) * 100,
      top: (y / VB_H) * 100,
      label: `${global.convert_month(d.bulan)} ${d.tahun}`,
      value: d.v,
    });
  };
  const handleLeave = () => setTip((prev) => ({ ...prev, show: false }));

  return (
    <div className="chart">
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        role="img"
        aria-label="Grafik pemakaian air bulanan dalam meter kubik"
      >
        <defs>
          <linearGradient id="ipBarGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#69B7F5" />
            <stop offset="1" stopColor="#1C62A8" />
          </linearGradient>
        </defs>
        <g className="grid">
          {ticks.map((g) => {
            const y = yOf(g);
            return (
              <React.Fragment key={g}>
                <line x1={PAD_L} y1={y} x2={VB_W - PAD_R} y2={y} />
                <text className="ylab" x={PAD_L - 6} y={y + 3} textAnchor="end">
                  {g}
                </text>
              </React.Fragment>
            );
          })}
        </g>
        <line
          className="avg"
          x1={PAD_L}
          y1={avgY}
          x2={VB_W - PAD_R}
          y2={avgY}
        />
        {data.map((d, i) => {
          const x = PAD_L + i * slotW + (slotW - barW) / 2;
          const y = yOf(d.v);
          const barH = Math.max(0, baseY - y);
          const cx = PAD_L + i * slotW + slotW / 2;
          const shortLabel = String(global.convert_month(d.bulan)).slice(0, 3);
          const isLast = i === data.length - 1;
          return (
            <g
              key={`${d.tahun}-${d.bulan}-${i}`}
              className="barwrap"
              onMouseEnter={() => handleEnter(d, cx, y)}
              onMouseLeave={handleLeave}
            >
              <rect
                className="bar"
                x={x}
                y={y}
                width={barW}
                height={barH}
                rx="5"
                fill="url(#ipBarGradient)"
                style={{ animationDelay: `${i * 60}ms` }}
              />
              <text className="val" x={cx} y={y - 6}>
                {d.v}
              </text>
              <text className="xlab" x={cx} y={baseY + 15}>
                {shortLabel}
              </text>
              {isLast && (
                <text className="nowlab" x={cx} y={baseY + 26}>
                  bulan ini
                </text>
              )}
              <rect
                className="hit"
                x={PAD_L + i * slotW}
                y={PAD_T}
                width={slotW}
                height={plotH}
              />
            </g>
          );
        })}
      </svg>
      {tip.show && (
        <div
          className="ip-chart-tip"
          style={{ left: `${tip.left}%`, top: `${tip.top}%` }}
        >
          <b>{tip.label}</b> · <span className="tv">{tip.value} m³</span>
        </div>
      )}
    </div>
  );
}

export default UsageChart;
